import React, { useMemo } from 'react';
import { TreeTaskCard } from 'shared/components/connected/task-card';
import {
  RenderItemProps,
  rootId,
  TreeItem,
  TreeItemTypes,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { useToday } from 'shared/contexts/today';
import { ID } from 'shared/types/id';
import { Task } from 'shared/types/task';

import { useTasksTree } from '../../hooks/use-tasks-tree';
import { GroupedListProps, noGroupingId } from '../../types';

const taskTreeItemTypes = [TreeItemTypes.Task];

export const NoGroupingTasksList: React.FC<GroupedListProps> = ({
  tasks,
  sortingMode,
  onReorder,
  customOrder = {},
  collapsedSections = [],
  onSectionCollapse,
  weekStartsOn,
  scrollElement,
}) => {
  const today = useToday();

  const tasksMap = useMemo(
    () =>
      tasks.reduce<Record<ID, Task>>((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}),
    [tasks],
  );

  const treeData = useTasksTree({
    tasks,
    sortingMode,
    customOrder,
    today,
    weekStartsOn,
  });

  const _onReorder = (updatedItems: TreeItem[]) => {
    if (!onReorder) {
      return;
    }
    const updatedRoot = updatedItems.find(({ id }) => id === rootId);
    if (!updatedRoot) {
      return;
    }

    onReorder(
      [{ id: noGroupingId, taskSorting: updatedRoot.children ?? [] }],
      [],
    );
  };

  const renderItem: React.FC<RenderItemProps> = ({
    item,
    tree,
    itemInstance,
  }) =>
    tasksMap[item.sourceId] ? (
      <TreeTaskCard
        task={tasksMap[item.sourceId]}
        tree={tree}
        item={itemInstance}
        tasks={tasks}
      />
    ) : null;

  return (
    <VirtualizedTreeList
      rootItemId={rootId}
      items={treeData}
      renderItem={renderItem}
      scrollElement={scrollElement}
      multiselectTypes={taskTreeItemTypes}
      onReorder={_onReorder}
      collapsedItems={collapsedSections}
      onCollapse={onSectionCollapse}
    />
  );
};
